// This file is generated by scripts/generators/generate-usecase-table-organisms.ts
import React from 'react'
import type { UsecaseTableOffices } from 'poc-ai-biz-irdb_domain-model'
import { UsecaseTable } from '../UsecaseTable'
import { UsecaseTableAccordion } from '../../UsecaseTableAccordion'

type Props = {
  contents: UsecaseTableOffices[]
  status: 'pending' | 'processing' | 'completed' | 'failed'
  defaultExpanded: boolean
}

export const UsecaseTableOfficesOrganism: React.FC<Props> = ({ contents, status, defaultExpanded = false }) => {
  // contentsをfilerNameとfiscalYearでグルーピング
  const groupedByCompanyAndYear = contents.reduce(
    (acc, item) => {
      const companyKey = `${item.filerName || ''}_${item.secCode || ''}`
      if (!acc[companyKey]) {
        acc[companyKey] = {
          filerName: item.filerName || '',
          secCode: item.secCode || '',
          years: {},
        }
      }
      const yearKey = item.fiscalYearEndYear.toString()
      if (!acc[companyKey].years[yearKey]) {
        acc[companyKey].years[yearKey] = []
      }
      acc[companyKey].years[yearKey].push(item)
      return acc
    },
    {} as Record<
      string,
      {
        filerName: string
        secCode: string
        years: Record<string, UsecaseTableOffices[]>
      }
    >,
  )

  // 企業ごとに最新年度のデータを取得
  const companiesWithItems: Array<{
    filerName: string
    secCode: string
    fiscalYearEndYear: number
    items: UsecaseTableOffices[]
  }> = []
  Object.values(groupedByCompanyAndYear).forEach((company) => {
    const years = Object.keys(company.years).sort((a, b) => parseInt(b) - parseInt(a))
    const latestYear = years[0]
    if (!latestYear) {
      return
    }
    const items = company.years[latestYear]
    if (!items) {
      return
    }

    // appearanceIndexでソート
    items.sort((a, b) => (a.appearanceIndex || 0) - (b.appearanceIndex || 0))

    companiesWithItems.push({
      filerName: company.filerName,
      secCode: company.secCode,
      fiscalYearEndYear: parseInt(latestYear),
      items,
    })
  })

  const formatValue = (value: string | number | string[] | object | null | undefined) => {
    if (!value) return '-'
    if (Array.isArray(value)) {
      return (
        <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
          {value.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      )
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2)
    }
    if (typeof value === 'number') {
      return value.toLocaleString()
    }
    return value
  }

  const formatNumber = (value: number | null | undefined, unit: string = '') => {
    if (!value) return '-'
    return `${value.toLocaleString()}${unit}`
  }

  // ローディング状態の表示
  if (status === 'pending' || status === 'processing') {
    return (
      <UsecaseTableAccordion summary="事業所等" defaultExpanded={defaultExpanded} disabled={true}>
        <span className="text-gray-500">読み込み中...</span>
      </UsecaseTableAccordion>
    )
  }

  // エラー状態の表示
  if (status === 'failed') {
    return (
      <UsecaseTableAccordion summary="事業所等" defaultExpanded={defaultExpanded} disabled={false}>
        <span className="text-red-600">データの読み込みに失敗しました</span>
      </UsecaseTableAccordion>
    )
  }

  return (
    <UsecaseTableAccordion summary="事業所等" defaultExpanded={defaultExpanded} disabled={false}>
      <div className="flex flex-col gap-6">
        {companiesWithItems.map((companyData, companyIndex) => (
          <div key={companyIndex} className="flex flex-col gap-2">
            <h3 className="text-sm font-bold">{companyData.filerName}</h3>
            <UsecaseTable>
              <UsecaseTable.Head>
                <UsecaseTable.Row>
                  {(
                    [
                      { name: 'officeName', displayName: '事業所名', type: 'string' },
                      { name: 'address', displayName: '所在地', type: 'string' },
                      { name: 'businessSegmentName', displayName: '事業セグメント名', type: 'string' },
                      { name: 'facilitiesSummary', displayName: '主な設備の内容', type: 'string' },
                      { name: 'bookValueUnit', displayName: '帳簿価格単位', type: 'string' },
                      { name: 'bookValueLandJpy', displayName: '帳簿価格(土地)', type: 'int' },
                      { name: 'landArea', displayName: '土地面積', type: 'int' },
                      { name: 'landAreaUnit', displayName: '土地面積単位', type: 'string' },
                      { name: 'bookValueBuildingJpy', displayName: '帳簿価格(建物)', type: 'int' },
                      { name: 'bookValueMachineryVehiclesEquipmentJpy', displayName: '帳簿価格(機械装置、 賃貸用車両及び器具)', type: 'int' },
                      { name: 'bookValueTotalJpy', displayName: '帳簿価格(合計)', type: 'int' },
                      { name: 'numberOfEmployees', displayName: '従業員数', type: 'int' },
                    ] as const
                  ).map((col) => (
                    <UsecaseTable.Cell key={col.name} type="text" component="th">
                      {col.displayName}
                    </UsecaseTable.Cell>
                  ))}
                </UsecaseTable.Row>
              </UsecaseTable.Head>
              <UsecaseTable.Body>
                {companyData.items.map((item, rowIndex) => (
                  <UsecaseTable.Row key={rowIndex}>
                    {(
                      [
                        { name: 'officeName', displayName: '事業所名', type: 'string' },
                        { name: 'address', displayName: '所在地', type: 'string' },
                        { name: 'businessSegmentName', displayName: '事業セグメント名', type: 'string' },
                        { name: 'facilitiesSummary', displayName: '主な設備の内容', type: 'string' },
                        { name: 'bookValueUnit', displayName: '帳簿価格単位', type: 'string' },
                        { name: 'bookValueLandJpy', displayName: '帳簿価格(土地)', type: 'int' },
                        { name: 'landArea', displayName: '土地面積', type: 'int' },
                        { name: 'landAreaUnit', displayName: '土地面積単位', type: 'string' },
                        { name: 'bookValueBuildingJpy', displayName: '帳簿価格(建物)', type: 'int' },
                        { name: 'bookValueMachineryVehiclesEquipmentJpy', displayName: '帳簿価格(機械装置、 賃貸用車両及び器具)', type: 'int' },
                        { name: 'bookValueTotalJpy', displayName: '帳簿価格(合計)', type: 'int' },
                        { name: 'numberOfEmployees', displayName: '従業員数', type: 'int' },
                      ] as const
                    ).map((col) => {
                      const cellType = ['int', 'float'].includes(col.type) ? 'currency' : 'text'
                      const value = formatValue(item?.[col.name as keyof UsecaseTableOffices])

                      return (
                        <UsecaseTable.Cell key={col.name} type={cellType}>
                          {value}
                        </UsecaseTable.Cell>
                      )
                    })}
                  </UsecaseTable.Row>
                ))}
              </UsecaseTable.Body>
            </UsecaseTable>
          </div>
        ))}
      </div>
    </UsecaseTableAccordion>
  )
}
